import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { HighlightWithinTextarea } from 'react-highlight-within-textarea';
import './App.css';
import {
  fmt,
  buildHighlightRanges,
  daysLeft,
  extractEntriesWithLabels,
  reconcileEntries,
} from './helpers/helpers.js';
import { normalizeEntryName } from './helpers/metadataHelpers';
import { updateEntryIdentity } from './helpers/api';
import LoginScreen from './LoginScreen';
import BillsTable from './components/BillsTable';
import IncomeTable from './components/IncomeTable';
import SuggestionsPanel from './components/SuggestionsPanel';
import MetadataManagementModal from './components/MetadataManagementModal';
import CardsModal from './components/CardsModal';
import ShareModal from './components/ShareModal';
import notesStore from './stores/NotesStore';

// User Menu Component
const UserMenu = observer(({ store, onSettingsClick, onCardsClick }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="user-menu">
      <button className="user-menu-trigger" onClick={() => setOpen(!open)}>
        <i className="fa-solid fa-circle-user"></i>
        <i className="fa-solid fa-chevron-down"></i>
      </button>

      {open && (
        <>
          <div className="user-menu-backdrop" onClick={() => setOpen(false)} />
          <div className="user-menu-dropdown">
            <div className="user-menu-header">
              <i className="fa-solid fa-user"></i>
              {store.isGuest ? 'Convidado' : store.userName || 'Usuário'}
            </div>
            <div className="user-menu-divider" />
            {!store.isGuest && (
              <button className="user-menu-item" onClick={() => { onSettingsClick(); setOpen(false); }}>
                <i className="fa-solid fa-tags"></i>
                Categorias
              </button>
            )}
            {!store.isGuest && (
              <button className="user-menu-item" onClick={() => { onCardsClick(); setOpen(false); }}>
                <i className="fa-solid fa-credit-card"></i>
                Cartões
              </button>
            )}
            <button className="user-menu-item" onClick={() => { store.logout(); setOpen(false); }}>
              <i className="fa-solid fa-right-from-bracket"></i>
              Sair
            </button>
          </div>
        </>
      )}
    </div>
  );
});

const API_URL = process.env.REACT_APP_API_URL;

const App = observer(({ sharedLinkToken }) => {
  const store = notesStore;

  // Shared note state (when viewing via share link)
  const [sharedNote, setSharedNote] = useState(null);
  const [sharedLoading, setSharedLoading] = useState(!!sharedLinkToken);
  const [sharedError, setSharedError] = useState(null);
  const isReadOnly = !!sharedLinkToken;

  // Local state for the editor
  const [localValue, setLocalValue] = useState('');
  const [isComposing, setIsComposing] = useState(false);
  const [isEditorFocused, setIsEditorFocused] = useState(false);
  const [metadataModalOpen, setMetadataModalOpen] = useState(false);
  const [cardsModalOpen, setCardsModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // Autocomplete state
  const [autocompleteQuery, setAutocompleteQuery] = useState('');
  const [autocompleteStart, setAutocompleteStart] = useState(-1);
  const [highlightedSuggestionIndex, setHighlightedSuggestionIndex] = useState(-1);
  const [caretPosition, setCaretPosition] = useState(0);
  const justInsertedRef = useRef(false);

  // Pending metadata links (for entries that don't have values yet)
  const [pendingMetadataLinks, setPendingMetadataLinks] = useState({});

  // Use metadata from store instead of loading separately
  const allMetadata = useMemo(() => {
    return Array.from(store.metadata.values());
  }, [store.metadata]);

  const editorWrapRef = useRef(null);
  const typingTimer = useRef(null);
  const lastHighlightsRef = useRef([]);

  // Load shared note when viewing via share link
  useEffect(() => {
    if (!sharedLinkToken) return;

    const loadSharedNote = async () => {
      try {
        setSharedLoading(true);
        setSharedError(null);

        const res = await fetch(`${API_URL}/api/shared/${sharedLinkToken}`);

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to load shared note');
        }

        const data = await res.json();
        setSharedNote(data);
        setLocalValue(data.content || '');
      } catch (err) {
        console.error('Error loading shared note:', err);
        setSharedError(err.message);
      } finally {
        setSharedLoading(false);
      }
    };

    loadSharedNote();
  }, [sharedLinkToken]);

  // Get current note content (use sharedNote when in shared mode)
  const currentNote = isReadOnly ? sharedNote : store.currentNote;
  const value = currentNote?.content || '';

  // Sync local value with current note (skip for shared notes - already set in loadSharedNote)
  useEffect(() => {
    if (isReadOnly) return;
    setLocalValue(value);
  }, [store.currentNoteId, value, isReadOnly]);

  // Debounced update from local value to store (skip for read-only shared notes)
  useEffect(() => {
    if (isReadOnly) return;
    if (typingTimer.current) clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => {
      if (localValue !== value && currentNote) {
        store.updateCurrentNote({ content: localValue });
      }
    }, 1000);
    return () => clearTimeout(typingTimer.current);
  }, [localValue, value, currentNote, store, isReadOnly]);

  // Note: Metadata is loaded by NotesStore when loading note details
  // We use store.metadata instead of loading separately

  // Parse entries from local value for textarea highlighting
  const { entries: localEntries } = useMemo(() => {
    const raw = extractEntriesWithLabels(localValue);
    return reconcileEntries(raw, currentNote || {});
  }, [localValue, currentNote]);

  // Get entries - use reconciled local entries for shared notes, store entries otherwise
  const entries = isReadOnly ? localEntries : store.entries;

  // Handle sample data initialization for first-time users
  useEffect(() => {
    if (!currentNote || !currentNote.isSampleData) return;

    const raw = extractEntriesWithLabels(currentNote.content);
    const { entryIndex, payments } = reconcileEntries(raw, currentNote);

    if (Object.keys(payments).length === 0) return;

    const twoDaysFromNow = new Date();
    twoDaysFromNow.setDate(twoDaysFromNow.getDate() + 2);
    const dueDateD2 = twoDaysFromNow.toISOString().slice(0, 10);

    const samplePayments = { ...payments };
    Object.entries(entryIndex).forEach(([entryId, entry]) => {
      // Set "fatura -40" with due date d+2
      if (entry.value === -40 && entry.lineIndex === 2) {
        samplePayments[entryId] = { is_paid: false, due_date: dueDateD2 };
      }
      // Set "uber -20" as paid
      if (entry.value === -20 && entry.lineIndex === 2) {
        samplePayments[entryId] = { is_paid: true, due_date: '' };
      }
    });

    store.updateCurrentNote({ payments: samplePayments, entryIndex, isSampleData: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNote?.id, currentNote?.isSampleData]);

  // Get computed values - calculate from entries for shared notes, use store otherwise
  const { negatives_paid, negatives_unpaid, sum, sum_in, sum_out, amount_still_left } = useMemo(() => {
    if (isReadOnly) {
      // Calculate sums from reconciled entries for shared notes.
      // A shared note is rendered without the viewer's payment sources, so the
      // card exclusion can only be applied to what the entry itself carries:
      // reminderOnly is on the payment record, but resolving paymentSourceId to
      // a kind needs the owner's source list, which is not shared. Erring
      // towards including it keeps the money visible rather than silently
      // dropping it from a total the viewer cannot audit.
      const cash = entries.filter(e => e.reminderOnly !== true);
      const paid = cash.filter(e => e.value < 0 && e.is_paid).reduce((a, e) => a + e.value, 0);
      const unpaid = cash.filter(e => e.value < 0 && !e.is_paid).reduce((a, e) => a + e.value, 0);
      const total = cash.reduce((a, e) => a + e.value, 0);
      const income = cash.filter(e => e.value > 0).reduce((a, e) => a + e.value, 0);
      const expenses = cash.filter(e => e.value < 0).reduce((a, e) => a + e.value, 0);
      const stillLeft = income + paid; // paid is negative, so adding subtracts
      return {
        negatives_paid: paid,
        negatives_unpaid: unpaid,
        sum: total,
        sum_in: income,
        sum_out: expenses,
        amount_still_left: stillLeft
      };
    }
    // Use store computed values for regular notes
    return {
      negatives_paid: store.negativesPaid,
      negatives_unpaid: store.negativesUnpaid,
      sum: store.sum,
      sum_in: store.sumIn,
      sum_out: store.sumOut,
      amount_still_left: store.amountStillLeft
    };
  }, [isReadOnly, entries, store.negativesPaid, store.negativesUnpaid, store.sum, store.sumIn, store.sumOut, store.amountStillLeft]);

  // Highlighting
  const highlightRanges = useMemo(() => {
    if (isEditorFocused || isComposing) return [];
    const h = buildHighlightRanges(localEntries);
    lastHighlightsRef.current = h;
    return h;
  }, [localEntries, isEditorFocused, isComposing]);

  const focusHighlightPatterns = useMemo(() => ([
    {
      highlight: /(?<![0-9A-Za-z])-\s*(?:R\$\s*)?(?:\d{1,3}(?:[.,]\d{3})+|\d+)(?:[.,]\d+)?(?![0-9A-Za-z])/g,
      className: 'red'
    },
    {
      highlight: /(?<![0-9A-Za-z-])(?:\+?\s*)?(?:R\$\s*)?(?:\d{1,3}(?:[.,]\d{3})+|\d+)(?:[.,]\d+)?(?![0-9A-Za-z])/g,
      className: 'green'
    }
  ]), []);

  // Filter autocomplete suggestions (disabled - using table link button instead)
  const autocompleteSuggestions = useMemo(() => {
    return [];
    // eslint-disable-next-line no-unreachable
    if (!autocompleteQuery || autocompleteQuery.length < 1) return [];

    const normalized = normalizeEntryName(autocompleteQuery);
    if (!normalized) return [];

    // Find current line index to check if entry already has metadata
    const beforeCaret = localValue.slice(0, caretPosition);
    const lineStart = beforeCaret.lastIndexOf('\n') + 1;
    const linesBefore = localValue.slice(0, lineStart).split('\n');
    const currentLineIndex = linesBefore.length - 1;

    // Find entry at current line
    const currentEntry = entries.find(e => e.lineIndex === currentLineIndex && e.value !== 0);
    const currentMetadataId = currentEntry?.metadataId;

    const suggestions = allMetadata
      .filter(m => {
        // Filter by normalized name prefix
        if (!m.normalizedName || !m.normalizedName.startsWith(normalized)) return false;

        // Filter out if this is the metadata already linked to current entry
        if (currentMetadataId && m.id === currentMetadataId) return false;

        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 10);

    console.log('[Autocomplete] Query:', autocompleteQuery, 'Line:', currentLineIndex, 'Current metadata:', currentMetadataId, 'Suggestions:', suggestions.length);
    return suggestions;
  }, [autocompleteQuery, allMetadata, localValue, caretPosition, entries]);

  // Badge updates — applied to stable mark.red elements (not via highlight classes)
  const soonThreshold = 3;
  // store.loading is included so the effect re-runs when the loading screen
  // goes away and the editor ref becomes available
  const storeLoading = store.loading;
  useEffect(() => {
    if (isEditorFocused || isComposing) return;
    const cancel = { done: false };
    const negEntries = localEntries.filter(e => e.value < 0 && !e.is_paid);
    let retries = 0;
    const applyBadges = () => {
      if (cancel.done) return;
      const root = editorWrapRef.current;
      if (!root) {
        // Editor not mounted yet (loading screen) — retry
        if (retries < 10) {
          retries++;
          setTimeout(applyBadges, 100);
        }
        return;
      }
      const marks = root.querySelectorAll('mark.red:not(.isPaid)');
      // Retry if draft-js hasn't rendered the marks yet
      if (marks.length < negEntries.length && retries < 10) {
        retries++;
        setTimeout(applyBadges, 100);
        return;
      }
      marks.forEach((markEl, i) => {
        const e = negEntries[i];
        markEl.classList.remove('negBadge', 'overdue', 'soon', 'future', 'hasLabel');
        markEl.removeAttribute('data-badge');
        if (!e) return;
        const dLeft = daysLeft(e.due_date);
        if (!dLeft || typeof dLeft.days !== 'number') return;
        const urgency = dLeft.days < 0 ? 'overdue' : dLeft.days <= soonThreshold ? 'soon' : 'future';
        markEl.classList.add('negBadge', urgency);
        markEl.setAttribute('data-badge', dLeft.adjusted ? dLeft.days + '*' : String(dLeft.days));
        if (e.label && e.label.trim().length > 0) markEl.classList.add('hasLabel');
      });
    };
    if (negEntries.length > 0) {
      setTimeout(applyBadges, 100);
    }
    return () => { cancel.done = true };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localEntries, isComposing, isEditorFocused, soonThreshold, storeLoading]);

  // Prune selection when entries change
  useEffect(() => {
    const validIds = new Set(entries.filter(e => e.value < 0).map(e => e.id));
    const newSelection = new Set();
    store.selectedIds.forEach(id => {
      if (validIds.has(id)) newSelection.add(id);
    });

    // Only update if selection changed
    if (newSelection.size !== store.selectedIds.size) {
      store.setSelection(newSelection);
    }
  }, [entries, store]);

  // Apply pending metadata links when entries are created
  useEffect(() => {
    const applyPendingLinks = async () => {
      if (!currentNote || !store.userId || store.isGuest) return;
      if (Object.keys(pendingMetadataLinks).length === 0) return;

      // Quick check: are there any entries that match pending links?
      const hasMatchingEntries = Object.keys(pendingMetadataLinks).some(lineIndexStr => {
        const lineIndex = parseInt(lineIndexStr, 10);
        return entries.some(e => e.lineIndex === lineIndex && e.value !== 0 && !e.metadataId);
      });

      if (!hasMatchingEntries) return; // Nothing to do

      const appliedLines = [];

      for (const [lineIndexStr, linkData] of Object.entries(pendingMetadataLinks)) {
        const lineIndex = parseInt(lineIndexStr, 10);

        // Find entry at this line that has a value
        const entry = entries.find(e => e.lineIndex === lineIndex && e.value !== 0);

        if (entry && !entry.metadataId) {
          try {
            // Determine entry kind
            const entryKind = entry.value < 0 ? 'expense' : 'income';

            console.log('[Autocomplete] Applying pending link at line', lineIndex, 'to entry', entry.id);

            // Wait a bit to ensure entry is in database
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Reload note to ensure we have the latest entry data
            await store.loadNoteDetails(store.currentNote.id);

            // Find the entry again after reload
            const freshEntries = store.entries;
            const freshEntry = freshEntries.find(e => e.lineIndex === lineIndex && e.value !== 0);

            if (freshEntry) {
              // Link entry to metadata
              await updateEntryIdentity(
                store.userId,
                store.currentNote.id,
                freshEntry.id,
                {
                  metadataId: linkData.metadataId,
                  kind: entryKind
                }
              );

              appliedLines.push(lineIndex);
              console.log('[Autocomplete] Successfully linked entry', freshEntry.id, 'to', linkData.metadataName);
            }
          } catch (error) {
            console.error('[Autocomplete] Error applying pending link:', error);
            // Don't mark as applied if it failed, will retry next time
          }
        }
      }

      // Remove applied pending links
      if (appliedLines.length > 0) {
        setPendingMetadataLinks(prev => {
          const updated = { ...prev };
          appliedLines.forEach(line => delete updated[line]);
          return updated;
        });

        // Final reload to show updated entry data (no need to reload metadata - we're just linking to existing)
        await store.loadNoteDetails(store.currentNote.id);

        console.log('[Autocomplete] Applied', appliedLines.length, 'pending links');
      }
    };

    applyPendingLinks();
  }, [entries, pendingMetadataLinks, currentNote, store]);

  // Get caret position from contenteditable div
  const getCaretPosition = useCallback((element) => {
    let caretOffset = 0;
    const doc = element.ownerDocument || element.document;
    const win = doc.defaultView || doc.parentWindow;
    const sel = win.getSelection();

    if (sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(element);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      caretOffset = preCaretRange.toString().length;
    }

    return caretOffset;
  }, []);

  // Parse label segment from current line
  const parseLineForAutocomplete = useCallback((text, caretPos) => {
    if (!text || caretPos < 0) {
      return { query: '', start: -1, shouldShow: false };
    }

    // Find line containing caret
    const beforeCaret = text.slice(0, caretPos);
    const lineStart = beforeCaret.lastIndexOf('\n') + 1;
    const lineEnd = text.indexOf('\n', caretPos);
    const currentLine = text.slice(lineStart, lineEnd === -1 ? text.length : lineEnd);
    const caretInLine = caretPos - lineStart;

    // Calculate line index for debugging
    const linesBefore = text.slice(0, lineStart).split('\n');
    const lineIndex = linesBefore.length - 1;

    // Find first number-like token in line (e.g., -1500, 1500, R$ 100)
    const numberMatch = currentLine.match(/(?:^|\s)([+-]?\s*(?:R\$\s*)?\d)/);

    if (!numberMatch) {
      // No number found - entire line could be label
      const query = currentLine.slice(0, caretInLine).trim();

      console.log('[Parse] Line', lineIndex, '- No number, query:', query, 'line:', currentLine);

      // Don't show autocomplete if query ends with value indicators (-, +, R$)
      // This means user is starting to type a value
      const endsWithValueIndicator = /[-+]$|R\$$/.test(query);

      return {
        query,
        start: lineStart,
        shouldShow: query.length >= 1 && !endsWithValueIndicator
      };
    }

    const numberStart = numberMatch.index + (numberMatch[0].length - numberMatch[1].length);

    // Check if caret is before the number (in label segment)
    if (caretInLine < numberStart) {
      const query = currentLine.slice(0, caretInLine).trim();

      console.log('[Parse] Line', lineIndex, '- In label, query:', query, 'numberStart:', numberStart, 'caretInLine:', caretInLine);

      return {
        query,
        start: lineStart,
        shouldShow: query.length >= 1
      };
    }

    // Caret is after number - don't show autocomplete
    console.log('[Parse] Line', lineIndex, '- After number, no autocomplete');
    return { query: '', start: -1, shouldShow: false };
  }, []);

  // Update autocomplete state based on caret position
  const handleTextareaChange = useCallback((newValue, event) => {
    setLocalValue(newValue);

    // Reset the "just inserted" flag when user types
    justInsertedRef.current = false;

    // Inline autocomplete disabled - using table link button instead
    // Get caret position only (no autocomplete parsing)
    setTimeout(() => {
      const contentEditable = editorWrapRef.current?.querySelector('[contenteditable="true"]');
      if (!contentEditable) return;
      const caret = getCaretPosition(contentEditable);
      setCaretPosition(caret);
    }, 0);
  }, [getCaretPosition]);

  // Track caret position on selection change (autocomplete disabled)
  const handleSelectionChange = useCallback(() => {
    const contentEditable = editorWrapRef.current?.querySelector('[contenteditable="true"]');
    if (!contentEditable) return;
    const caret = getCaretPosition(contentEditable);
    setCaretPosition(caret);
  }, [getCaretPosition]);

  // Insert selected suggestion
  const insertSuggestion = useCallback(async (suggestion) => {
    if (!suggestion || autocompleteStart < 0) return;

    // Get the contenteditable to access current caret position
    const contentEditable = editorWrapRef.current?.querySelector('[contenteditable="true"]');
    if (!contentEditable) return;

    // Get CURRENT caret position from DOM (not from state)
    const currentCaretPos = getCaretPosition(contentEditable);
    const currentValue = localValue;

    console.log('[Insert] Current caret:', currentCaretPos, 'value length:', currentValue.length);

    // Find current line boundaries using current caret position
    const beforeCaret = currentValue.slice(0, currentCaretPos);
    const lineStart = beforeCaret.lastIndexOf('\n') + 1;
    const lineEnd = currentValue.indexOf('\n', currentCaretPos);
    const actualLineEnd = lineEnd === -1 ? currentValue.length : lineEnd;

    console.log('[Insert] Line start:', lineStart, 'Line end:', actualLineEnd);

    // Get current line
    const currentLine = currentValue.slice(lineStart, actualLineEnd);

    // Find first number in line to determine label segment end
    const numberMatch = currentLine.match(/(?:^|\s)([+-]?\s*(?:R\$\s*)?\d)/);
    const labelEnd = numberMatch
      ? lineStart + numberMatch.index + (numberMatch[0].length - numberMatch[1].length)
      : actualLineEnd;

    // Replace label segment with suggestion name
    const newValue =
      currentValue.slice(0, lineStart) +
      suggestion.name + ' ' +
      currentValue.slice(labelEnd);

    console.log('[Insert] New value length:', newValue.length);

    setLocalValue(newValue);

    // Clear autocomplete state
    setAutocompleteQuery('');
    setAutocompleteStart(-1);
    setHighlightedSuggestionIndex(-1);

    // Set flag to prevent autocomplete from re-opening immediately
    justInsertedRef.current = true;

    // Calculate line index
    const linesBefore = currentValue.slice(0, lineStart).split('\n');
    const lineIndex = linesBefore.length - 1;

    // Create updated pending links with this new link
    const updatedPendingLinks = {
      ...pendingMetadataLinks,
      [lineIndex]: {
        metadataId: suggestion.id,
        metadataName: suggestion.name
      }
    };

    // Store locally for the effect
    setPendingMetadataLinks(updatedPendingLinks);

    // Send to backend immediately with content update
    // Use store.currentNote to get the latest value
    if (store.currentNote) {
      store.updateCurrentNote({
        content: newValue,
        pendingMetadataLinks: updatedPendingLinks
      });
    }

    console.log('[Autocomplete] Stored pending link at line', lineIndex, 'for', suggestion.name);

    // Set cursor position after inserted text
    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      setTimeout(() => {
        const contentEditable = editorWrapRef.current?.querySelector('[contenteditable="true"]');
        if (!contentEditable) return;

        // Calculate desired cursor position in the NEW value
        // lineStart is from old value, but everything before lineStart stays the same
        const desiredPosition = lineStart + suggestion.name.length + 1;

        console.log('[Autocomplete] Setting cursor to position', desiredPosition, 'lineStart:', lineStart, 'name length:', suggestion.name.length);

        // Get the actual text content from DOM to verify
        const actualText = contentEditable.textContent || '';
        console.log('[Autocomplete] DOM text length:', actualText.length, 'desired:', desiredPosition);

        // Set selection at that position
        const range = document.createRange();
        const sel = window.getSelection();

        // Find the text node and offset using TreeWalker
        let currentPos = 0;
        let targetNode = null;
        let targetOffset = 0;

        const walker = document.createTreeWalker(
          contentEditable,
          NodeFilter.SHOW_TEXT,
          null,
          false
        );

        while (walker.nextNode()) {
          const node = walker.currentNode;
          const nodeLength = node.textContent.length;

          if (currentPos + nodeLength >= desiredPosition) {
            targetNode = node;
            targetOffset = desiredPosition - currentPos;
            console.log('[Autocomplete] Found target node at', currentPos, 'offset:', targetOffset, 'node text:', node.textContent.substring(0, 20));
            break;
          }

          currentPos += nodeLength;
        }

        if (targetNode) {
          try {
            range.setStart(targetNode, targetOffset);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
            contentEditable.focus();
            console.log('[Autocomplete] Cursor positioned successfully');
          } catch (error) {
            console.error('[Autocomplete] Error setting cursor:', error);
          }
        } else {
          console.error('[Autocomplete] Could not find target node for position', desiredPosition);
        }

        // Reset the flag after cursor is positioned
        setTimeout(() => {
          justInsertedRef.current = false;
        }, 100);
      }, 0);
    });
  }, [localValue, autocompleteStart, pendingMetadataLinks, getCaretPosition, store]);

  // Handle keyboard navigation for autocomplete
  const handleAutocompleteKeyDown = useCallback((e) => {
    // Only handle if autocomplete is visible
    if (autocompleteSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedSuggestionIndex(prev =>
          prev < autocompleteSuggestions.length - 1 ? prev + 1 : 0
        );
        break;

      case 'ArrowUp':
        e.preventDefault();
        setHighlightedSuggestionIndex(prev =>
          prev > 0 ? prev - 1 : autocompleteSuggestions.length - 1
        );
        break;

      case 'Enter':
        if (highlightedSuggestionIndex >= 0 && highlightedSuggestionIndex < autocompleteSuggestions.length) {
          e.preventDefault();
          insertSuggestion(autocompleteSuggestions[highlightedSuggestionIndex]);
        }
        break;

      case 'Escape':
        e.preventDefault();
        setAutocompleteQuery('');
        setAutocompleteStart(-1);
        setHighlightedSuggestionIndex(-1);
        break;

      default:
        break;
    }
  }, [autocompleteSuggestions, highlightedSuggestionIndex, insertSuggestion]);

  // Event handlers
  const handleLogin = async (token, user) => {
    // Guest mode: onLogin is called with just the guest id as `token` and no `user`
    if (!user) {
      await store.startSession(token, null, null);
      return;
    }

    // Check if we need to migrate guest data
    if (store.isGuest && store.hasNotes) {
      const result = await store.migrateGuestData(token, user.id);
      if (!result.ok) {
        alert('Erro ao migrar dados. Os dados locais foram preservados.');
      }
    } else {
      await store.startSession(user.id, null, token);
    }
  };

  const renameTitle = () => {
    if (!currentNote) return;
    const newTitle = prompt('Novo título:', currentNote.title);
    if (!newTitle) return;
    store.renameNote(currentNote.id, newTitle);
  };

  const handleShare = () => {
    if (!currentNote) return;
    setShareModalOpen(true);
  };

  // Show loading screen (for shared notes or regular loading)
  if (sharedLoading || (!isReadOnly && store.loading)) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: 'system-ui, sans-serif'
      }}>
        <h2>{isReadOnly ? 'Carregando nota compartilhada...' : 'Carregando notas...'}</h2>
      </div>
    );
  }

  // Show error for shared notes
  if (isReadOnly && sharedError) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: 'system-ui, sans-serif',
        textAlign: 'center',
        padding: '20px'
      }}>
        <h2 style={{ color: '#f44336' }}>Nota não encontrada</h2>
        <p style={{ color: '#666' }}>{sharedError}</p>
        <p style={{ color: '#999', fontSize: '0.9em' }}>O link pode estar desativado ou a nota foi excluída.</p>
      </div>
    );
  }

  // Show login screen if not authenticated (skip for shared notes)
  if (!isReadOnly && !store.userId) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  return (
    <div className="app">
      <header>
        <div className="topheaderrow">
          <h1 className="totalTitle">
            <span className={sum < 0 ? 'sum-negative' : 'sum-positive'}>{fmt(sum)}</span>
            <span style={{
              fontSize: '0.5em',
              marginLeft: '12px',
              color: amount_still_left < 0 ? 'var(--expense-color)' : 'var(--income-color)',
              fontWeight: 'normal',
              fontStyle: 'italic'
            }}>
              {fmt(amount_still_left)}
            </span>
          </h1>
          {isReadOnly ? (
            <div className="shared-indicator">
              <i className="fa-solid fa-link"></i> Nota Compartilhada
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <UserMenu
                store={store}
                onSettingsClick={() => setMetadataModalOpen(true)}
                onCardsClick={() => setCardsModalOpen(true)}
              />
              <button className="menuButton header-menu-btn" onClick={() => store.setModalOpen(true)}>
                <i className="fa-solid fa-bars"></i>
              </button>
            </div>
          )}
        </div>

        <div className="sumGroup" role="group" aria-label="Resumo das somas">
          <div className="sumItem sumIn" title="Entradas">
            <span className="sumLabel">
              <i className="fa-solid fa-arrow-down"></i>
              Entradas
            </span>
            <span className="sumValue">+{fmt(sum_in)}</span>
          </div>

          <div className="sumItem sumOut" title="Saídas">
            <span className="sumLabel">
              <i className="fa-solid fa-arrow-up"></i>
              Saídas
            </span>
            <span className="sumValue">{fmt(sum_out)}</span>
          </div>

          <div className="sumItem sumPaid" title="Pagos">
            <span className="sumLabel">
              <i className="fa-solid fa-circle-check"></i>
              Pago
            </span>
            <span className="sumValue"><del>{fmt(negatives_paid)}</del></span>
          </div>

          <div className="sumItem sumUnpaid" title="A pagar">
            <span className="sumLabel">
              <i className="fa-regular fa-clock"></i>
              A pagar
            </span>
            <span className="sumValue">{fmt(negatives_unpaid)}</span>
          </div>
        </div>

        {!isReadOnly && store.syncStatus && !store.isGuest && (
          <div className={`sync-indicator sync-${store.syncStatus}`}>
            {store.syncStatus === 'synced' && (
              <><i className="fa-solid fa-cloud"></i> Sincronizado</>
            )}
            {store.syncStatus === 'syncing' && (
              <><i className="fa-solid fa-rotate fa-spin"></i> Sincronizando...</>
            )}
            {store.syncStatus === 'error' && (
              <><i className="fa-solid fa-triangle-exclamation"></i> Erro de sincronização</>
            )}
          </div>
        )}
      </header>

      <main>
        {/* Desktop tabs - only show on larger screens (hidden for shared notes) */}
        {!isReadOnly && (
          <div className="notes-tabs">
            <div className="tabs-list-wrapper">
              <div className="tabs-list">
                {store.notes.map(note => (
                  <button
                    key={note.id}
                    className={`tab-item ${note.id === store.currentNoteId ? 'active' : ''}`}
                    onClick={() => store.setCurrentNoteId(note.id)}
                    title={note.title}
                  >
                    {note.title}
                  </button>
                ))}
                {/* Spacer to prevent last tab from being hidden behind fixed buttons */}
                <div className="tabs-spacer"></div>
              </div>
              <div className="tabs-fixed-buttons">
                <button
                  className="tab-item new-tab-fixed"
                  onClick={() => store.addNote()}
                  title="Nova nota"
                >
                  <i className="fa-solid fa-plus"></i>
                </button>
                <button
                  className="tab-item manage-tab-fixed"
                  onClick={() => store.setModalOpen(true)}
                  title="Ver todas as notas"
                >
                  <i className="fa-solid fa-layer-group"></i>
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="note-header">
          <h2 className="note-title" onClick={isReadOnly ? undefined : renameTitle} title={isReadOnly ? '' : 'Clique para renomear'}>
            {currentNote?.title || 'Sem nota selecionada'}
          </h2>
          {isReadOnly ? (
            <span className="readonly-badge" title="Nota compartilhada - somente leitura">
              <i className="fa-solid fa-eye"></i> Somente leitura
            </span>
          ) : (
            <div className="note-actions">
              <button className="icon-btn" onClick={renameTitle} title="Renomear">
                <i className="fa-solid fa-pencil"></i>
              </button>
              <button className="icon-btn" onClick={handleShare} title="Compartilhar">
                <i className="fa-solid fa-share-nodes"></i>
              </button>
              <button
                className="icon-btn icon-btn-danger"
                onClick={() => {
                  if (window.confirm(`Excluir nota "${currentNote?.title}"?`)) {
                    store.deleteNote(currentNote.id);
                  }
                }}
                title="Excluir nota"
              >
                <i className="fa-solid fa-trash-can"></i>
              </button>
            </div>
          )}
        </div>

        <div ref={editorWrapRef} style={{ position: 'relative' }}>
          <HighlightWithinTextarea
              editorKey="notes-editor"
              value={localValue}
              onChange={isReadOnly ? () => {} : handleTextareaChange}
              highlight={(isEditorFocused || isComposing) ? focusHighlightPatterns : highlightRanges}
              style={{ width: '100%', height: '220px' }}
              onCompositionStart={() => setIsComposing(true)}
              onCompositionEnd={() => setIsComposing(false)}
              onFocus={() => setIsEditorFocused(true)}
              onBlur={() => {
                setIsEditorFocused(false);
                if (!isReadOnly && localValue !== value && currentNote) {
                  store.updateCurrentNote({ content: localValue });
                }
              }}
              onClick={handleSelectionChange}
              onKeyUp={handleSelectionChange}
              /* onKeyDown={isReadOnly ? undefined : handleAutocompleteKeyDown} // disabled inline autocomplete */
              autoCorrect="off"
              spellCheck={false}
              autoCapitalize="off"
              readOnly={isReadOnly}
            />

          {/* Autocomplete Dropdown */}
          {autocompleteSuggestions.length > 0 && (
            <div className="inline-autocomplete-dropdown">
              {autocompleteSuggestions.map((suggestion, index) => (
                <div
                  key={suggestion.id}
                  className={`inline-autocomplete-item ${
                    index === highlightedSuggestionIndex ? 'highlighted' : ''
                  }`}
                  onMouseDown={(e) => {
                    e.preventDefault(); // Prevent blur
                    insertSuggestion(suggestion);
                  }}
                  onMouseEnter={() => setHighlightedSuggestionIndex(index)}
                >
                  <span className="suggestion-name">{suggestion.name}</span>
                  <span className="suggestion-badge">
                    {suggestion.kind === 'income' ? '+' : '-'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <div style={{ marginTop: '2rem' }}>
        <BillsTable readOnly={isReadOnly} sharedNote={sharedNote} />
      </div>

      {!isReadOnly && <div style={{ marginTop: '2rem' }}><IncomeTable /></div>}

      {!isReadOnly && <div style={{ marginTop: '2rem' }}><SuggestionsPanel /></div>}

      {store.modalOpen && (
        <>
          <div className="drawer-backdrop" onClick={() => store.setModalOpen(false)} />
          <div className="notes-drawer">
            <div className="drawer-header">
              <h3>Notas</h3>
              <div className="drawer-actions">
                <button
                  className="icon-btn"
                  onClick={() => store.setModalOpen(false)}
                  title="Fechar"
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>
            </div>

            {/* View Toggle: Manual / Most Recent (only for logged-in users) */}
            {!store.isGuest && (
              <div className="view-toggle">
                <button
                  className={`view-toggle-btn ${store.notesSortBy === 'manual' ? 'active' : ''}`}
                  onClick={() => store.setNotesSortBy('manual')}
                >
                  <i className="fa-solid fa-grip-vertical"></i> Manual
                </button>
                <button
                  className={`view-toggle-btn ${store.notesSortBy === 'recent' ? 'active' : ''}`}
                  onClick={() => store.setNotesSortBy('recent')}
                >
                  <i className="fa-solid fa-clock-rotate-left"></i> Recente
                </button>
              </div>
            )}

            <button className="new-note-btn" onClick={() => store.addNote()}>
              <i className="fa-solid fa-plus"></i>
              Nova Nota
            </button>

            <ul className="notes-list">
              {store.notes.map((note, index) => (
                <li
                  key={note.id}
                  className={`note-item ${note.id === store.currentNoteId ? 'active' : ''} ${
                    !store.isGuest && store.notesSortBy === 'manual' ? 'sort-mode' : ''
                  }`}
                >
                  {!store.isGuest && store.notesSortBy === 'manual' && (
                    <div className="sort-handle">
                      <button
                        className="sort-btn"
                        onClick={() => store.moveNote(note.id, 'up')}
                        disabled={index === 0}
                        title="Mover para cima"
                      >
                        <i className="fa-solid fa-chevron-up"></i>
                      </button>
                      <button
                        className="sort-btn"
                        onClick={() => store.moveNote(note.id, 'down')}
                        disabled={index === store.notes.length - 1}
                        title="Mover para baixo"
                      >
                        <i className="fa-solid fa-chevron-down"></i>
                      </button>
                    </div>
                  )}

                  <button
                    className="note-select-btn"
                    onClick={() => {
                      store.setCurrentNoteId(note.id);
                      store.setModalOpen(false);
                    }}
                  >
                    <span className="note-title">{note.title}</span>
                  </button>

                  <button
                    className="note-delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(`Excluir nota "${note.title}"?`)) {
                        store.deleteNote(note.id);
                      }
                    }}
                    title="Excluir nota"
                  >
                    <i className="fa-solid fa-trash-can"></i>
                  </button>
                </li>
              ))}
            </ul>

            {!store.isGuest && store.notesSortBy === 'manual' && (
              <div className="sort-hint">
                <i className="fa-solid fa-circle-info"></i>
                Use as setas para reordenar
              </div>
            )}
          </div>
        </>
      )}

      <MetadataManagementModal
        isOpen={metadataModalOpen}
        onClose={() => setMetadataModalOpen(false)}
        userId={store.userId}
      />

      <CardsModal
        isOpen={cardsModalOpen}
        onClose={() => setCardsModalOpen(false)}
        userId={store.userId}
      />

      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        noteId={currentNote?.id}
        noteTitle={currentNote?.title}
        store={store}
      />
    </div>
  );
});

export default App;
